import { SHOPIFY_API_KEY, THEME_APP_EMBED_HANDLE } from "../config/env";

/** Section block handle — `blocks/booster.liquid` */
export const BOOSTER_BLOCK_HANDLE = "booster";

const TEMPLATE_BY_PLACEMENT = {
  product_page: "product",
  cart_page: "cart",
  announcement_bar: "index",
  home_page: "index",
};

/**
 * Placement → theme editor template + section target (Calendar-style addAppBlockId).
 * `mainSection` drops the block into the page's primary content area.
 */
const PLACEMENT_TO_EDITOR_TARGET = {
  product_page: { template: "product", target: "mainSection", previewPath: null },
  cart_page: { template: "cart", target: "mainSection", previewPath: "/cart" },
  home_page: { template: "index", target: "mainSection", previewPath: "/" },
  announcement_bar: { template: "index", target: "mainSection", previewPath: "/" },
};

function shopNameFromDomain(shop) {
  if (!shop) return "";
  let s = String(shop).trim();
  if (s.includes(".myshopify.com")) {
    s = s.replace(/\.myshopify\.com.*$/i, "");
  }
  const storePrefix = s.match(/^store\/([^/]+)/i);
  if (storePrefix) return storePrefix[1];
  return s.split("/")[0] || "";
}

/**
 * Theme editor deep link — placement-aware (product / cart / home / announcement).
 * Opens App embeds panel with activateAppId for the free shipping bar block.
 */
export function getThemeEditorDeepLink(shop, placementType = "product_page") {
  if (!shop || !SHOPIFY_API_KEY) return null;
  const storeHandle = shopNameFromDomain(shop);
  if (!storeHandle) return null;

  const template = TEMPLATE_BY_PLACEMENT[placementType] || "index";
  const handle = THEME_APP_EMBED_HANDLE || "free-shipping-bar";
  const adminBase = `https://${storeHandle}.myshopify.com/admin`;
  const appEmbedParam = encodeURIComponent(`${SHOPIFY_API_KEY}/${handle}`);

  return `${adminBase}/themes/current/editor?context=apps&template=${template}&activateAppId=${appEmbedParam}`;
}

/**
 * Theme editor deep link that adds the Free Shipping Bar section block to the page template.
 * Matches Calendar app `addAppBlockId` + `target=mainSection` behaviour.
 */
export function getThemeEditorBlockDeepLink(shop, placementType = "product_page") {
  if (!shop || !SHOPIFY_API_KEY) return null;
  const storeHandle = shopNameFromDomain(shop);
  if (!storeHandle) return null;

  const targetCfg =
    PLACEMENT_TO_EDITOR_TARGET[placementType] || PLACEMENT_TO_EDITOR_TARGET.product_page;

  const addAppBlockId = `${SHOPIFY_API_KEY}/${BOOSTER_BLOCK_HANDLE}`;
  const search = new URLSearchParams({
    template: targetCfg.template,
    addAppBlockId,
    target: targetCfg.target,
  });
  if (targetCfg.previewPath) {
    search.set("previewPath", targetCfg.previewPath);
  }

  return `https://${storeHandle}.myshopify.com/admin/themes/current/editor?${search.toString()}`;
}

export const PLACEMENT_OPTIONS = [
  {
    id: "product_page",
    title: "Product page",
    description: "Automatically below Add to cart (or use a custom theme block).",
  },
  {
    id: "cart_page",
    title: "Cart page",
    description: "Automatically above cart items (or use a custom theme block).",
  },
  {
    id: "announcement_bar",
    title: "Store-wide bar",
    description: "Fixed bar at the top of every page — app embed, no block needed.",
  },
  {
    id: "home_page",
    title: "Home page",
    description: "Automatically below the header on home (or use a custom theme block).",
  },
];

/** All placements offered when creating a new bar. */
export const CREATION_PLACEMENT_OPTIONS = PLACEMENT_OPTIONS;

export function getPlacementLabel(placementType) {
  const match = PLACEMENT_OPTIONS.find((p) => p.id === placementType);
  return match?.title || "Product page";
}

export const PRESET_BY_PLACEMENT = {
  product_page: "product_inline_minimal",
  cart_page: "cart_page_standard",
  announcement_bar: "announcement_top_slim",
  home_page: "home_page_banner",
};
